import { request } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// ==================== Product Development & R&D ====================

// R&D Projects
export async function getRDProjects(params?: { status?: string; type?: string; customerId?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development${queryString ? `?${queryString}` : ''}`);
}

export async function getRDProject(id: string) {
  return request<any>(`/product-development/${id}`);
}

export async function createRDProject(data: {
  projectName: string;
  projectType?: string;
  category?: string;
  description?: string;
  objectives?: any[];
  targetMarket?: string;
  customerId?: string;
  budget?: string;
  startDate?: string;
  targetCompletion?: string;
}) {
  return request<any>('/product-development', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDProject(id: string, data: Partial<{
  projectName: string;
  status: string;
  stage: string;
  priority: string;
  description: string;
  budget: string;
  spentAmount: string;
  teamMembers: any[];
}>) {
  return request<any>(`/product-development/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRDProject(id: string) {
  return request<{ success: boolean }>(`/product-development/${id}`, { method: 'DELETE' });
}

// R&D Milestones
export async function getRDMilestones(params?: { projectId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/milestones/list${queryString ? `?${queryString}` : ''}`);
}

export async function createRDMilestone(data: {
  projectId: string;
  milestoneName: string;
  milestoneType?: string;
  description?: string;
  targetDate?: string;
  deliverables?: any[];
}) {
  return request<any>('/product-development/milestones', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDMilestone(id: string, data: Partial<{
  milestoneName: string;
  status: string;
  targetDate: string;
  actualDate: string;
  completionNotes: string;
}>) {
  return request<any>(`/product-development/milestones/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// R&D Prototypes
export async function getRDPrototypes(params?: { projectId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/prototypes${queryString ? `?${queryString}` : ''}`);
}

export async function getRDPrototype(id: string) {
  return request<any>(`/product-development/prototypes/${id}`);
}

export async function createRDPrototype(data: {
  projectId: string;
  prototypeName: string;
  description?: string;
  formulaId?: string;
  ingredients?: any[];
  sensoryProfile?: any;
  technicalSpecs?: any;
}) {
  return request<any>('/product-development/prototypes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDPrototype(id: string, data: Partial<{
  prototypeName: string;
  status: string;
  developmentStage: string;
  ingredients: any[];
  sensoryProfile: any;
  performanceData: any;
}>) {
  return request<any>(`/product-development/prototypes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Prototype Iterations
export async function getPrototypeIterations(prototypeId: string) {
  return request<{ data: any[] }>(`/product-development/prototypes/${prototypeId}/iterations`);
}

export async function createPrototypeIteration(prototypeId: string, data: {
  iterationReason?: string;
  changesMade?: any[];
  ingredientsAdded?: any[];
  ingredientsRemoved?: any[];
  ingredientsModified?: any[];
  testResults?: any;
}) {
  return request<any>(`/product-development/prototypes/${prototypeId}/iterations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Ingredient Discovery
export async function getIngredientDiscoveries(params?: { status?: string; type?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/discoveries${queryString ? `?${queryString}` : ''}`);
}

export async function createIngredientDiscovery(data: {
  discoveryName: string;
  discoveryType?: string;
  category?: string;
  description?: string;
  source?: string;
  materialName?: string;
  supplierId?: string;
  estimatedCost?: string;
  potentialApplications?: any[];
}) {
  return request<any>('/product-development/discoveries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateIngredientDiscovery(id: string, data: Partial<{
  evaluationStatus: string;
  sampleStatus: string;
  evaluationNotes: string;
  technicalData: any;
  sensoryData: any;
}>) {
  return request<any>(`/product-development/discoveries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Market Trends
export async function getMarketTrends(params?: { type?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/trends${queryString ? `?${queryString}` : ''}`);
}

export async function createMarketTrend(data: {
  trendName: string;
  trendType?: string;
  category?: string;
  description?: string;
  source?: string;
  impactLevel?: string;
  opportunities?: any[];
  actionItems?: any[];
}) {
  return request<any>('/product-development/trends', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMarketTrend(id: string, data: Partial<{
  trendName: string;
  status: string;
  impactLevel: string;
  opportunities: any[];
  actionItems: any[];
}>) {
  return request<any>(`/product-development/trends/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Competitive Intelligence
export async function getCompetitiveIntel(params?: { competitor?: string; type?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.competitor) query.set('competitor', params.competitor);
  if (params?.type) query.set('type', params.type);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/competitive-intel${queryString ? `?${queryString}` : ''}`);
}

export async function createCompetitiveIntel(data: {
  competitorName: string;
  intelType?: string;
  title: string;
  description?: string;
  productName?: string;
  threatLevel?: string;
  responseStrategy?: string;
}) {
  return request<any>('/product-development/competitive-intel', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCompetitiveIntel(id: string, data: Partial<{
  title: string;
  threatLevel: string;
  responseStrategy: string;
  responseActions: any[];
}>) {
  return request<any>(`/product-development/competitive-intel/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Sensory Panels
export async function getRDSensoryPanels(params?: { projectId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/panels${queryString ? `?${queryString}` : ''}`);
}

export async function getRDSensoryPanel(id: string) {
  return request<any>(`/product-development/panels/${id}`);
}

export async function createRDSensoryPanel(data: {
  panelName: string;
  panelType?: string;
  projectId?: string;
  prototypeIds?: string[];
  methodology?: string;
  evaluationCriteria?: any[];
  scheduledDate?: string;
}) {
  return request<any>('/product-development/panels', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDSensoryPanel(id: string, data: Partial<{
  panelName: string;
  status: string;
  results: any;
  conclusions: string;
  recommendations: any[];
}>) {
  return request<any>(`/product-development/panels/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Panel Responses
export async function getPanelResponses(panelId: string) {
  return request<{ data: any[] }>(`/product-development/panels/${panelId}/responses`);
}

export async function createPanelResponse(panelId: string, data: {
  panelistId?: string;
  panelistName?: string;
  prototypeId?: string;
  sampleCode?: string;
  responses: any;
  overallScore?: number;
  comments?: string;
}) {
  return request<any>(`/product-development/panels/${panelId}/responses`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Technical Documentation
export async function getRDTechnicalDocs(params?: { projectId?: string; prototypeId?: string; type?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.prototypeId) query.set('prototypeId', params.prototypeId);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/docs${queryString ? `?${queryString}` : ''}`);
}

export async function createRDTechnicalDoc(data: {
  projectId?: string;
  prototypeId?: string;
  docType: string;
  title: string;
  description?: string;
  content?: string;
  authors?: any[];
}) {
  return request<any>('/product-development/docs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDTechnicalDoc(id: string, data: Partial<{
  title: string;
  status: string;
  content: string;
  version: string;
}>) {
  return request<any>(`/product-development/docs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Innovation Pipeline
export async function getInnovationPipeline(params?: { stage?: string; type?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.stage) query.set('stage', params.stage);
  if (params?.type) query.set('type', params.type);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/pipeline${queryString ? `?${queryString}` : ''}`);
}

export async function getInnovationIdea(id: string) {
  return request<any>(`/product-development/pipeline/${id}`);
}

export async function createInnovationIdea(data: {
  ideaName: string;
  ideaType?: string;
  category?: string;
  description?: string;
  problemStatement?: string;
  proposedSolution?: string;
  targetCustomer?: string;
  valueProposition?: string;
}) {
  return request<any>('/product-development/pipeline', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInnovationIdea(id: string, data: Partial<{
  ideaName: string;
  description: string;
  businessCase: any;
  riskAssessment: any;
}>) {
  return request<any>(`/product-development/pipeline/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function advanceInnovationIdea(id: string, data: { stage: string; decision?: string; notes?: string }) {
  return request<any>(`/product-development/pipeline/${id}/advance`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function convertIdeaToProject(id: string, projectData?: { projectName?: string }) {
  return request<any>(`/product-development/pipeline/${id}/convert`, {
    method: 'POST',
    body: JSON.stringify(projectData || {}),
  });
}

// Lab Experiments
export async function getRDExperiments(params?: { projectId?: string; prototypeId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.prototypeId) query.set('prototypeId', params.prototypeId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/experiments${queryString ? `?${queryString}` : ''}`);
}

export async function createRDExperiment(data: {
  projectId?: string;
  prototypeId?: string;
  experimentName: string;
  experimentType?: string;
  hypothesis?: string;
  methodology?: string;
  materials?: any[];
  procedure?: string;
}) {
  return request<any>('/product-development/experiments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDExperiment(id: string, data: Partial<{
  experimentName: string;
  status: string;
  rawData: any;
  results: any;
  analysis: string;
  conclusions: string;
}>) {
  return request<any>(`/product-development/experiments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Resource Allocation
export async function getRDResourceAllocations(params?: { projectId?: string; type?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectId) query.set('projectId', params.projectId);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/product-development/resources${queryString ? `?${queryString}` : ''}`);
}

export async function createRDResourceAllocation(data: {
  projectId: string;
  resourceType: string;
  resourceName: string;
  role?: string;
  allocationPercentage?: number;
  startDate?: string;
  endDate?: string;
}) {
  return request<any>('/product-development/resources', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRDResourceAllocation(id: string, data: Partial<{
  allocationPercentage: number;
  status: string;
  actualHours: number;
  actualCost: string;
}>) {
  return request<any>(`/product-development/resources/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRDResourceAllocation(id: string) {
  return request<{ success: boolean }>(`/product-development/resources/${id}`, { method: 'DELETE' });
}

// Product Development Dashboard
export async function getProductDevelopmentDashboard() {
  return request<{
    totalProjects: number;
    activeProjects: number;
    projectsByStatus: Record<string, number>;
    prototypesByStatus: Record<string, number>;
    pipelineByStage: Record<string, number>;
    recentProjects: any[];
    upcomingMilestones: any[];
    pendingDiscoveries: any[];
  }>('/product-development/dashboard');
}

// ==================== Supplier Portal ====================

// Portal Accounts
export async function getSupplierPortalAccounts(params?: { supplierId?: string; isActive?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/accounts${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierPortalAccount(data: {
  supplierId: string;
  email: string;
  name: string;
  role?: string;
  phone?: string;
  department?: string;
}) {
  return request<any>('/supplier-portal/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierPortalAccount(id: string, data: Partial<{
  name: string;
  role: string;
  isActive: boolean;
  portalPermissions: any[];
}>) {
  return request<any>(`/supplier-portal/accounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSupplierPortalAccount(id: string) {
  return request<{ success: boolean }>(`/supplier-portal/accounts/${id}`, { method: 'DELETE' });
}

// Bid Requests
export async function getSupplierBidRequests(params?: { status?: string; type?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/bid-requests${queryString ? `?${queryString}` : ''}`);
}

export async function getSupplierBidRequest(id: string) {
  return request<any>(`/supplier-portal/bid-requests/${id}`);
}

export async function createSupplierBidRequest(data: {
  title: string;
  description?: string;
  requestType?: string;
  items: any[];
  invitedSuppliers?: string[];
  deliveryLocation?: string;
  deliveryDate?: string;
  closeDate?: string;
  evaluationCriteria?: any[];
}) {
  return request<any>('/supplier-portal/bid-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierBidRequest(id: string, data: Partial<{
  title: string;
  description: string;
  items: any[];
  closeDate: string;
}>) {
  return request<any>(`/supplier-portal/bid-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function publishBidRequest(id: string) {
  return request<any>(`/supplier-portal/bid-requests/${id}/publish`, { method: 'POST' });
}

export async function closeBidRequest(id: string) {
  return request<any>(`/supplier-portal/bid-requests/${id}/close`, { method: 'POST' });
}

export async function awardBidRequest(id: string, data: { supplierId: string; amount?: string }) {
  return request<any>(`/supplier-portal/bid-requests/${id}/award`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Bids
export async function getSupplierBids(params?: { bidRequestId?: string; supplierId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.bidRequestId) query.set('bidRequestId', params.bidRequestId);
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/bids${queryString ? `?${queryString}` : ''}`);
}

export async function getSupplierBid(id: string) {
  return request<any>(`/supplier-portal/bids/${id}`);
}

export async function createSupplierBid(data: {
  bidRequestId: string;
  supplierId: string;
  items: any[];
  totalAmount?: string;
  deliveryDate?: string;
  notes?: string;
}) {
  return request<any>('/supplier-portal/bids', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierBid(id: string, data: Partial<{
  items: any[];
  totalAmount: string;
  notes: string;
}>) {
  return request<any>(`/supplier-portal/bids/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function evaluateSupplierBid(id: string, data: { score: number; scoringDetails?: any; feedback?: string }) {
  return request<any>(`/supplier-portal/bids/${id}/evaluate`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Performance Metrics
export async function getSupplierMetrics(params?: { supplierId?: string; periodType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.periodType) query.set('periodType', params.periodType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/metrics${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierMetrics(data: {
  supplierId: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  onTimeDeliveryRate?: string;
  qualityAcceptanceRate?: string;
  overallScore?: number;
  grade?: string;
}) {
  return request<any>('/supplier-portal/metrics', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Scorecards
export async function getSupplierScorecards(params?: { supplierId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/scorecards${queryString ? `?${queryString}` : ''}`);
}

export async function getSupplierScorecard(id: string) {
  return request<any>(`/supplier-portal/scorecards/${id}`);
}

export async function createSupplierScorecard(data: {
  supplierId: string;
  scorecardType: string;
  periodStart: string;
  periodEnd: string;
  categories: any[];
  overallScore?: number;
  grade?: string;
}) {
  return request<any>('/supplier-portal/scorecards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierScorecard(id: string, data: Partial<{
  categories: any[];
  overallScore: number;
  grade: string;
  improvementAreas: any[];
}>) {
  return request<any>(`/supplier-portal/scorecards/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function publishSupplierScorecard(id: string) {
  return request<any>(`/supplier-portal/scorecards/${id}/publish`, { method: 'POST' });
}

// Supplier Portal Documents
export async function getSupplierPortalDocuments(params?: { supplierId?: string; type?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/documents${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierPortalDocument(data: {
  supplierId: string;
  documentType: string;
  documentName: string;
  filePath?: string;
  fileUrl?: string;
  effectiveDate?: string;
  expiryDate?: string;
}) {
  return request<any>('/supplier-portal/documents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function reviewSupplierPortalDocument(id: string, data: { status: string; notes?: string }) {
  return request<any>(`/supplier-portal/documents/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Action Items
export async function getSupplierActionItems(params?: { supplierId?: string; status?: string; priority?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.status) query.set('status', params.status);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/action-items${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierActionItem(data: {
  supplierId: string;
  title: string;
  description?: string;
  category?: string;
  priority?: string;
  dueDate?: string;
}) {
  return request<any>('/supplier-portal/action-items', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierActionItem(id: string, data: Partial<{
  title: string;
  status: string;
  correctiveAction: string;
  completedDate: string;
}>) {
  return request<any>(`/supplier-portal/action-items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Supplier Portal Messages
export async function getSupplierPortalMessages(params?: { supplierId?: string; threadId?: string; type?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.threadId) query.set('threadId', params.threadId);
  if (params?.type) query.set('type', params.type);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/messages${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierPortalMessage(data: {
  supplierId: string;
  threadId?: string;
  messageType?: string;
  subject?: string;
  content: string;
  senderName?: string;
}) {
  return request<any>('/supplier-portal/messages', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Price Lists
export async function getSupplierPortalPriceLists(params?: { supplierId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/price-lists${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierPortalPriceList(data: {
  supplierId: string;
  listName: string;
  items: any[];
  effectiveDate?: string;
  expiryDate?: string;
}) {
  return request<any>('/supplier-portal/price-lists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function reviewSupplierPriceList(id: string, data: { status: string; notes?: string }) {
  return request<any>(`/supplier-portal/price-lists/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Supplier Capacity
export async function getSupplierCapacity(params?: { supplierId?: string; materialId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.materialId) query.set('materialId', params.materialId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/capacity${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierCapacity(data: {
  supplierId: string;
  materialId?: string;
  periodType: string;
  periodStart: string;
  totalCapacity?: string;
  availableCapacity?: string;
}) {
  return request<any>('/supplier-portal/capacity', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierCapacity(id: string, data: Partial<{
  totalCapacity: string;
  availableCapacity: string;
  leadTimeDays: number;
}>) {
  return request<any>(`/supplier-portal/capacity/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Supplier Announcements
export async function getSupplierAnnouncements(params?: { isPublished?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.isPublished !== undefined) query.set('isPublished', params.isPublished.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/announcements${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierAnnouncement(data: {
  title: string;
  content: string;
  announcementType?: string;
  priority?: string;
  targetAudience?: string;
  targetSuppliers?: string[];
  expiresAt?: string;
}) {
  return request<any>('/supplier-portal/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierAnnouncement(id: string, data: Partial<{
  title: string;
  content: string;
  priority: string;
}>) {
  return request<any>(`/supplier-portal/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function publishSupplierAnnouncement(id: string) {
  return request<any>(`/supplier-portal/announcements/${id}/publish`, { method: 'POST' });
}

// Supplier Collaboration Requests
export async function getSupplierCollaborationRequests(params?: { supplierId?: string; status?: string; type?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/supplier-portal/collaboration-requests${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierCollaborationRequest(data: {
  supplierId: string;
  requestType: string;
  subject: string;
  description?: string;
  priority?: string;
  requestedDate?: string;
}) {
  return request<any>('/supplier-portal/collaboration-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierCollaborationRequest(id: string, data: Partial<{
  status: string;
  scheduledDate: string;
  assignedTo: string;
  responseNotes: string;
}>) {
  return request<any>(`/supplier-portal/collaboration-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Supplier Portal Dashboard
export async function getSupplierPortalDashboard() {
  return request<{
    activeAccounts: number;
    activeBidRequests: number;
    pendingBids: number;
    pendingDocuments: number;
    openActionItems: number;
    recentActivity: any[];
  }>('/supplier-portal/dashboard');
}

// ==================== QUALITY ANALYTICS ====================

// Prediction Models
export async function getQAPredictionModels(params?: { status?: string; modelType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.modelType) query.set('modelType', params.modelType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/models${queryString ? `?${queryString}` : ''}`);
}

export async function getQAPredictionModel(id: string) {
  return request<{ data: any }>(`/quality-analytics/models/${id}`);
}

export async function createQAPredictionModel(data: {
  modelName: string;
  modelType: string;
  targetVariable: string;
  description?: string;
  inputFeatures?: any[];
  algorithm?: string;
  hyperparameters?: any;
  trainingDataConfig?: any;
}) {
  return request<any>('/quality-analytics/models', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQAPredictionModel(id: string, data: Partial<{
  modelName: string;
  description: string;
  status: string;
  inputFeatures: any[];
  hyperparameters: any;
  performanceMetrics: any;
  featureImportance: any[];
}>) {
  return request<any>(`/quality-analytics/models/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteQAPredictionModel(id: string) {
  return request<any>(`/quality-analytics/models/${id}`, { method: 'DELETE' });
}

export async function trainQAPredictionModel(id: string) {
  return request<any>(`/quality-analytics/models/${id}/train`, { method: 'POST' });
}

export async function deployQAPredictionModel(id: string) {
  return request<any>(`/quality-analytics/models/${id}/deploy`, { method: 'POST' });
}

// QA Predictions
export async function getQAPredictions(params?: { modelId?: string; referenceType?: string; referenceId?: string; riskLevel?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.modelId) query.set('modelId', params.modelId);
  if (params?.referenceType) query.set('referenceType', params.referenceType);
  if (params?.referenceId) query.set('referenceId', params.referenceId);
  if (params?.riskLevel) query.set('riskLevel', params.riskLevel);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/predictions${queryString ? `?${queryString}` : ''}`);
}

export async function createQAPrediction(data: {
  modelId: string;
  predictionType: string;
  referenceType?: string;
  referenceId?: string;
  referenceName?: string;
  inputData: any;
}) {
  return request<any>('/quality-analytics/predictions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function recordQAPredictionActual(id: string, data: {
  actualValue: string;
  feedback?: string;
}) {
  return request<any>(`/quality-analytics/predictions/${id}/actual`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Defect Patterns
export async function getQADefectPatterns(params?: { status?: string; patternType?: string; severity?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.patternType) query.set('patternType', params.patternType);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/defect-patterns${queryString ? `?${queryString}` : ''}`);
}

export async function getQADefectPattern(id: string) {
  return request<{ data: any }>(`/quality-analytics/defect-patterns/${id}`);
}

export async function createQADefectPattern(data: {
  patternName: string;
  patternType: string;
  description?: string;
  defectCategory?: string;
  defectCodes?: string[];
  severity?: string;
  rootCauses?: any[];
  contributingFactors?: any[];
}) {
  return request<any>('/quality-analytics/defect-patterns', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQADefectPattern(id: string, data: Partial<{
  patternName: string;
  description: string;
  status: string;
  severity: string;
  rootCauses: any[];
  preventiveActions: any[];
  resolution: string;
}>) {
  return request<any>(`/quality-analytics/defect-patterns/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Trend Analysis
export async function getQATrendAnalyses(params?: { analysisType?: string; scope?: string; scopeId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.analysisType) query.set('analysisType', params.analysisType);
  if (params?.scope) query.set('scope', params.scope);
  if (params?.scopeId) query.set('scopeId', params.scopeId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/trends${queryString ? `?${queryString}` : ''}`);
}

export async function getQATrendAnalysis(id: string) {
  return request<{ data: any }>(`/quality-analytics/trends/${id}`);
}

export async function createQATrendAnalysis(data: {
  analysisName: string;
  analysisType: string;
  metricName?: string;
  scope?: string;
  scopeId?: string;
  scopeName?: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
}) {
  return request<any>('/quality-analytics/trends', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Process Capability
export async function getQAProcessCapabilities(params?: { processId?: string; capabilityRating?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.processId) query.set('processId', params.processId);
  if (params?.capabilityRating) query.set('capabilityRating', params.capabilityRating);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/capability${queryString ? `?${queryString}` : ''}`);
}

export async function getQAProcessCapability(id: string) {
  return request<{ data: any }>(`/quality-analytics/capability/${id}`);
}

export async function createQAProcessCapability(data: {
  analysisName: string;
  processName: string;
  processId?: string;
  parameterName: string;
  specificationLower?: string;
  specificationUpper?: string;
  specificationTarget?: string;
  unit?: string;
  periodStart?: string;
  periodEnd?: string;
  dataPoints?: any[];
}) {
  return request<any>('/quality-analytics/capability', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Cost Analysis
export async function getQACostAnalyses(params?: { analysisType?: string; periodStart?: string; periodEnd?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.analysisType) query.set('analysisType', params.analysisType);
  if (params?.periodStart) query.set('periodStart', params.periodStart);
  if (params?.periodEnd) query.set('periodEnd', params.periodEnd);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/costs${queryString ? `?${queryString}` : ''}`);
}

export async function getQACostAnalysis(id: string) {
  return request<{ data: any }>(`/quality-analytics/costs/${id}`);
}

export async function createQACostAnalysis(data: {
  analysisName: string;
  analysisType: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  preventionCosts?: any;
  appraisalCosts?: any;
  internalFailureCosts?: any;
  externalFailureCosts?: any;
  revenue?: string;
}) {
  return request<any>('/quality-analytics/costs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Alert Rules
export async function getQAAlertRules(params?: { isActive?: boolean; ruleType?: string; severity?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.ruleType) query.set('ruleType', params.ruleType);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/alert-rules${queryString ? `?${queryString}` : ''}`);
}

export async function getQAAlertRule(id: string) {
  return request<{ data: any }>(`/quality-analytics/alert-rules/${id}`);
}

export async function createQAAlertRule(data: {
  ruleName: string;
  ruleType: string;
  metricName: string;
  conditionType: string;
  thresholdValue?: string;
  thresholdUpper?: string;
  thresholdLower?: string;
  severity: string;
  description?: string;
  metricSource?: string;
  scope?: string;
  scopeFilter?: any;
  notificationChannels?: string[];
  notificationRecipients?: any[];
  cooldownMinutes?: number;
}) {
  return request<any>('/quality-analytics/alert-rules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQAAlertRule(id: string, data: Partial<{
  ruleName: string;
  description: string;
  isActive: boolean;
  thresholdValue: string;
  thresholdUpper: string;
  thresholdLower: string;
  severity: string;
  notificationChannels: string[];
  notificationRecipients: any[];
  cooldownMinutes: number;
}>) {
  return request<any>(`/quality-analytics/alert-rules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteQAAlertRule(id: string) {
  return request<any>(`/quality-analytics/alert-rules/${id}`, { method: 'DELETE' });
}

// Alert History
export async function getQAAlerts(params?: { ruleId?: string; severity?: string; acknowledged?: boolean; resolved?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.ruleId) query.set('ruleId', params.ruleId);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.acknowledged !== undefined) query.set('acknowledged', params.acknowledged.toString());
  if (params?.resolved !== undefined) query.set('resolved', params.resolved.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/alerts${queryString ? `?${queryString}` : ''}`);
}

export async function acknowledgeQAAlert(id: string) {
  return request<any>(`/quality-analytics/alerts/${id}/acknowledge`, { method: 'POST' });
}

export async function resolveQAAlert(id: string, data: { resolutionNotes?: string; isFalsePositive?: boolean }) {
  return request<any>(`/quality-analytics/alerts/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Benchmarks
export async function getQABenchmarks(params?: { benchmarkType?: string; category?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.benchmarkType) query.set('benchmarkType', params.benchmarkType);
  if (params?.category) query.set('category', params.category);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/benchmarks${queryString ? `?${queryString}` : ''}`);
}

export async function getQABenchmark(id: string) {
  return request<{ data: any }>(`/quality-analytics/benchmarks/${id}`);
}

export async function createQABenchmark(data: {
  benchmarkName: string;
  benchmarkType: string;
  metricName: string;
  benchmarkValue: string;
  category?: string;
  metricUnit?: string;
  scope?: string;
  scopeId?: string;
  benchmarkSource?: string;
  effectiveDate?: string;
  expiryDate?: string;
  currentPerformance?: string;
}) {
  return request<any>('/quality-analytics/benchmarks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQABenchmark(id: string, data: Partial<{
  benchmarkName: string;
  benchmarkValue: string;
  currentPerformance: string;
  gapAnalysis: string;
  status: string;
  improvementPlan: any[];
}>) {
  return request<any>(`/quality-analytics/benchmarks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Improvement Projects
export async function getQAImprovementProjects(params?: { status?: string; projectType?: string; priority?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.projectType) query.set('projectType', params.projectType);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/projects${queryString ? `?${queryString}` : ''}`);
}

export async function getQAImprovementProject(id: string) {
  return request<{ data: any }>(`/quality-analytics/projects/${id}`);
}

export async function createQAImprovementProject(data: {
  projectName: string;
  projectType: string;
  methodology?: string;
  description?: string;
  problemStatement?: string;
  scope?: string;
  objectives?: any[];
  targetMetrics?: any[];
  priority?: string;
  sponsorId?: string;
  sponsorName?: string;
  leadId?: string;
  leadName?: string;
  teamMembers?: any[];
  startDate?: string;
  targetCompletion?: string;
  budget?: string;
}) {
  return request<any>('/quality-analytics/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQAImprovementProject(id: string, data: Partial<{
  projectName: string;
  description: string;
  status: string;
  priority: string;
  rootCauses: any[];
  solutions: any[];
  actionItems: any[];
  milestones: any[];
  results: any;
  lessonsLearned: string;
  actualCompletion: string;
  actualCost: string;
  savingsActual: string;
}>) {
  return request<any>(`/quality-analytics/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Scorecards
export async function getQAScorecards(params?: { scorecardType?: string; scope?: string; scopeId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.scorecardType) query.set('scorecardType', params.scorecardType);
  if (params?.scope) query.set('scope', params.scope);
  if (params?.scopeId) query.set('scopeId', params.scopeId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/scorecards${queryString ? `?${queryString}` : ''}`);
}

export async function getQAScorecard(id: string) {
  return request<{ data: any }>(`/quality-analytics/scorecards/${id}`);
}

export async function createQAScorecard(data: {
  scorecardName: string;
  scorecardType: string;
  scope?: string;
  scopeId?: string;
  scopeName?: string;
  periodStart: string;
  periodEnd: string;
  metrics: any[];
}) {
  return request<any>('/quality-analytics/scorecards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function approveQAScorecard(id: string) {
  return request<any>(`/quality-analytics/scorecards/${id}/approve`, { method: 'POST' });
}

// Data Snapshots
export async function getQADataSnapshots(params?: { snapshotType?: string; referenceId?: string; usedForTraining?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.snapshotType) query.set('snapshotType', params.snapshotType);
  if (params?.referenceId) query.set('referenceId', params.referenceId);
  if (params?.usedForTraining !== undefined) query.set('usedForTraining', params.usedForTraining.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/quality-analytics/snapshots${queryString ? `?${queryString}` : ''}`);
}

export async function createQADataSnapshot(data: {
  snapshotType: string;
  referenceId: string;
  referenceName?: string;
  features: any;
  qualityScore?: number;
  defectCount?: number;
  defectTypes?: string[];
  testResults?: any;
  processParameters?: any;
  environmentalConditions?: any;
  materialProperties?: any;
  equipmentStatus?: any;
  outcomeLabel?: string;
}) {
  return request<any>('/quality-analytics/snapshots', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function markSnapshotForTraining(id: string, modelId: string) {
  return request<any>(`/quality-analytics/snapshots/${id}/training`, {
    method: 'PUT',
    body: JSON.stringify({ modelId }),
  });
}

// Quality Analytics Dashboard
export async function getQualityAnalyticsDashboard() {
  return request<{
    deployedModels: number;
    activeAlertRules: number;
    unresolvedAlerts: number;
    activeProjects: number;
    recentPredictions: any[];
    activeDefectPatterns: any[];
    latestScorecard: any;
  }>('/quality-analytics/dashboard');
}

// ==================== CUSTOMER RELATIONSHIP PORTAL ====================

// Portal Accounts
export async function getCustomerPortalAccounts(params?: { customerId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/accounts${queryString ? `?${queryString}` : ''}`);
}

export async function getCustomerPortalAccount(id: string) {
  return request<{ data: any }>(`/customer-relationship-portal/accounts/${id}`);
}

export async function createCustomerPortalAccount(data: {
  customerId: string;
  userEmail: string;
  userName: string;
  role?: string;
  permissions?: string[];
}) {
  return request<any>('/customer-relationship-portal/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerPortalAccount(id: string, data: Partial<{
  userName: string;
  role: string;
  permissions: string[];
  status: string;
  notificationPreferences: any;
}>) {
  return request<any>(`/customer-relationship-portal/accounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deactivateCustomerPortalAccount(id: string) {
  return request<any>(`/customer-relationship-portal/accounts/${id}/deactivate`, { method: 'POST' });
}

// Order Tracking
export async function getCustomerOrderTracking(params?: { customerId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/order-tracking${queryString ? `?${queryString}` : ''}`);
}

export async function getCustomerOrderTrackingById(id: string) {
  return request<{ data: any }>(`/customer-relationship-portal/order-tracking/${id}`);
}

export async function createCustomerOrderTracking(data: {
  orderId: string;
  customerId: string;
  orderNumber?: string;
  orderDate?: string;
  status?: string;
  estimatedShipDate?: string;
  lineItems?: any[];
}) {
  return request<any>('/customer-relationship-portal/order-tracking', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerOrderTracking(id: string, data: Partial<{
  status: string;
  trackingNumber: string;
  carrier: string;
  actualShipDate: string;
  actualDeliveryDate: string;
  productionProgress: any;
  qualityStatus: string;
  notes: string;
}>) {
  return request<any>(`/customer-relationship-portal/order-tracking/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Sample Requests
export async function getCustomerSampleRequests(params?: { customerId?: string; status?: string; requestType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.requestType) query.set('requestType', params.requestType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/sample-requests${queryString ? `?${queryString}` : ''}`);
}

export async function getCustomerSampleRequest(id: string) {
  return request<{ data: any }>(`/customer-relationship-portal/sample-requests/${id}`);
}

export async function createCustomerSampleRequest(data: {
  customerId: string;
  requestType: string;
  products: any[];
  priority?: string;
  application?: string;
  targetMarket?: string;
  specialRequirements?: string;
  deadline?: string;
  shippingAddress?: any;
}) {
  return request<any>('/customer-relationship-portal/sample-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerSampleRequest(id: string, data: Partial<{
  status: string;
  assignedTo: string;
  internalNotes: string;
  trackingNumber: string;
  shippingMethod: string;
  rejectionReason: string;
}>) {
  return request<any>(`/customer-relationship-portal/sample-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function submitCustomerSampleFeedback(id: string, data: { rating: number; comments?: string }) {
  return request<any>(`/customer-relationship-portal/sample-requests/${id}/feedback`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// CRP Documents
export async function getCRPDocuments(params?: { customerId?: string; documentType?: string; referenceType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.documentType) query.set('documentType', params.documentType);
  if (params?.referenceType) query.set('referenceType', params.referenceType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/documents${queryString ? `?${queryString}` : ''}`);
}

export async function uploadCRPDocument(data: {
  customerId: string;
  documentType: string;
  documentName: string;
  description?: string;
  filePath?: string;
  fileSize?: number;
  mimeType?: string;
  referenceType?: string;
  referenceId?: string;
  effectiveDate?: string;
  expiryDate?: string;
}) {
  return request<any>('/customer-relationship-portal/documents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function recordCRPDocumentDownload(id: string) {
  return request<any>(`/customer-relationship-portal/documents/${id}/download`, { method: 'POST' });
}

export async function deleteCRPDocument(id: string) {
  return request<any>(`/customer-relationship-portal/documents/${id}`, { method: 'DELETE' });
}

// Product Access
export async function getCustomerProductAccess(params?: { customerId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/product-access${queryString ? `?${queryString}` : ''}`);
}

export async function grantCustomerProductAccess(data: {
  customerId: string;
  accessType: string;
  formulaId?: string;
  category?: string;
  expiresAt?: string;
  canViewPricing?: boolean;
  canViewComposition?: boolean;
  canViewRegulatory?: boolean;
  canRequestSamples?: boolean;
  canPlaceOrders?: boolean;
  notes?: string;
}) {
  return request<any>('/customer-relationship-portal/product-access', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerProductAccess(id: string, data: Partial<{
  expiresAt: string;
  canViewPricing: boolean;
  canViewComposition: boolean;
  canViewRegulatory: boolean;
  canRequestSamples: boolean;
  canPlaceOrders: boolean;
  notes: string;
}>) {
  return request<any>(`/customer-relationship-portal/product-access/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function revokeCustomerProductAccess(id: string) {
  return request<any>(`/customer-relationship-portal/product-access/${id}`, { method: 'DELETE' });
}

// Price Agreements
export async function getCustomerPriceAgreements(params?: { customerId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/price-agreements${queryString ? `?${queryString}` : ''}`);
}

export async function getCustomerPriceAgreement(id: string) {
  return request<{ data: any }>(`/customer-relationship-portal/price-agreements/${id}`);
}

export async function createCustomerPriceAgreement(data: {
  customerId: string;
  agreementType: string;
  effectiveDate: string;
  expiryDate?: string;
  currency?: string;
  priceItems: any[];
  volumeTiers?: any[];
  paymentTerms?: string;
  deliveryTerms?: string;
  specialConditions?: string;
}) {
  return request<any>('/customer-relationship-portal/price-agreements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerPriceAgreement(id: string, data: Partial<{
  status: string;
  priceItems: any[];
  volumeTiers: any[];
  paymentTerms: string;
  deliveryTerms: string;
  specialConditions: string;
  actualVolume: string;
  actualValue: string;
}>) {
  return request<any>(`/customer-relationship-portal/price-agreements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveCustomerPriceAgreement(id: string) {
  return request<any>(`/customer-relationship-portal/price-agreements/${id}/approve`, { method: 'POST' });
}

// Messages
export async function getCustomerPortalMessages(params?: { customerId?: string; threadId?: string; messageType?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.threadId) query.set('threadId', params.threadId);
  if (params?.messageType) query.set('messageType', params.messageType);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/messages${queryString ? `?${queryString}` : ''}`);
}

export async function sendCustomerPortalMessage(data: {
  customerId: string;
  subject?: string;
  message: string;
  messageType?: string;
  threadId?: string;
  senderName?: string;
  referenceType?: string;
  referenceId?: string;
  priority?: string;
  attachments?: any[];
}) {
  return request<any>('/customer-relationship-portal/messages', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function markCustomerMessageAsRead(id: string) {
  return request<any>(`/customer-relationship-portal/messages/${id}/read`, { method: 'POST' });
}

// Support Tickets
export async function getCustomerSupportTickets(params?: { customerId?: string; status?: string; category?: string; priority?: string; assignedTo?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.category) query.set('category', params.category);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.assignedTo) query.set('assignedTo', params.assignedTo);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/tickets${queryString ? `?${queryString}` : ''}`);
}

export async function getCustomerSupportTicket(id: string) {
  return request<{ data: any }>(`/customer-relationship-portal/tickets/${id}`);
}

export async function createCustomerSupportTicket(data: {
  customerId: string;
  category: string;
  subcategory?: string;
  subject: string;
  description: string;
  priority?: string;
  referenceType?: string;
  referenceId?: string;
  referenceNumber?: string;
  attachments?: any[];
}) {
  return request<any>('/customer-relationship-portal/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerSupportTicket(id: string, data: Partial<{
  status: string;
  priority: string;
  assignedTo: string;
  escalated: boolean;
  escalatedTo: string;
  escalationReason: string;
}>) {
  return request<any>(`/customer-relationship-portal/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addCustomerTicketComment(ticketId: string, data: {
  content: string;
  commentType?: string;
  commenterName?: string;
  attachments?: any[];
  isInternal?: boolean;
}) {
  return request<any>(`/customer-relationship-portal/tickets/${ticketId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function resolveCustomerSupportTicket(id: string, data: { resolution: string; resolutionType?: string }) {
  return request<any>(`/customer-relationship-portal/tickets/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function closeCustomerSupportTicket(id: string) {
  return request<any>(`/customer-relationship-portal/tickets/${id}/close`, { method: 'POST' });
}

// Announcements
export async function getCustomerAnnouncements(params?: { status?: string; announcementType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.announcementType) query.set('announcementType', params.announcementType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/announcements${queryString ? `?${queryString}` : ''}`);
}

export async function createCustomerAnnouncement(data: {
  title: string;
  content: string;
  announcementType?: string;
  priority?: string;
  targetAudience?: string;
  targetCustomerIds?: string[];
  targetRegions?: string[];
  targetSegments?: string[];
  publishDate?: string;
  expiryDate?: string;
  requiresAcknowledgment?: boolean;
  attachments?: any[];
}) {
  return request<any>('/customer-relationship-portal/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerAnnouncement(id: string, data: Partial<{
  title: string;
  content: string;
  announcementType: string;
  priority: string;
  targetAudience: string;
  publishDate: string;
  expiryDate: string;
  status: string;
}>) {
  return request<any>(`/customer-relationship-portal/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function publishCustomerAnnouncement(id: string) {
  return request<any>(`/customer-relationship-portal/announcements/${id}/publish`, { method: 'POST' });
}

// Activity Log
export async function getCustomerPortalActivity(params?: { customerId?: string; accountId?: string; activityType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.accountId) query.set('accountId', params.accountId);
  if (params?.activityType) query.set('activityType', params.activityType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/activity${queryString ? `?${queryString}` : ''}`);
}

export async function logCustomerPortalActivity(data: {
  customerId: string;
  accountId?: string;
  activityType: string;
  activityCategory?: string;
  description: string;
  referenceType?: string;
  referenceId?: string;
  ipAddress?: string;
  userAgent?: string;
  details?: any;
}) {
  return request<any>('/customer-relationship-portal/activity', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Analytics
export async function getCustomerAnalytics(params?: { customerId?: string; periodType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.periodType) query.set('periodType', params.periodType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/customer-relationship-portal/analytics${queryString ? `?${queryString}` : ''}`);
}

export async function createCustomerAnalytics(data: {
  customerId: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  orderCount?: number;
  orderValue?: string;
  sampleRequests?: number;
  supportTickets?: number;
  satisfactionScore?: number;
  engagementScore?: number;
  churnRisk?: string;
}) {
  return request<any>('/customer-relationship-portal/analytics', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Preferences
export async function getCustomerPreferences(customerId: string) {
  return request<{ data: any }>(`/customer-relationship-portal/preferences/${customerId}`);
}

export async function updateCustomerPreferences(customerId: string, data: Partial<{
  communicationPreferences: any;
  notificationTypes: string[];
  language: string;
  timezone: string;
  dateFormat: string;
  currencyDisplay: string;
  dashboardWidgets: any[];
  reportPreferences: any;
  defaultShippingAddress: any;
  defaultBillingAddress: any;
  autoReorderEnabled: boolean;
  autoReorderRules: any[];
}>) {
  return request<any>(`/customer-relationship-portal/preferences/${customerId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Dashboard
export async function getCustomerRelationshipPortalDashboard() {
  return request<{
    activeAccounts: number;
    pendingOrders: number;
    pendingSamples: number;
    openTickets: number;
    recentActivity: any[];
    recentAnnouncements: any[];
  }>('/customer-relationship-portal/dashboard');
}

// ==========================================
// Phase 46: Advanced Inventory Management
// ==========================================

// Storage Zones (Advanced Inventory)
export async function getAdvancedStorageZones(params?: PaginationParams & { warehouseId?: string; zoneType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  if (params?.zoneType) searchParams.set('zoneType', params.zoneType);
  return request<PaginatedResponse<any>>(`/advanced-inventory/zones?${searchParams}`);
}

export async function getAdvancedStorageZone(id: string) {
  return request<any>(`/advanced-inventory/zones/${id}`);
}

export async function createAdvancedStorageZone(data: {
  warehouseId: string;
  code: string;
  name: string;
  zoneType: string;
  temperatureMin?: string;
  temperatureMax?: string;
  humidityMin?: string;
  humidityMax?: string;
  hazardousAllowed?: boolean;
  maxWeight?: string;
  description?: string;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/zones', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdvancedStorageZone(id: string, data: Partial<{
  name: string;
  zoneType: string;
  temperatureMin: string;
  temperatureMax: string;
  humidityMin: string;
  humidityMax: string;
  hazardousAllowed: boolean;
  maxWeight: string;
  isActive: boolean;
  description: string;
  metadata: any;
}>) {
  return request<any>(`/advanced-inventory/zones/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAdvancedStorageZone(id: string) {
  return request<any>(`/advanced-inventory/zones/${id}`, { method: 'DELETE' });
}

// Storage Bins (Advanced Inventory)
export async function getAdvancedStorageBins(params?: PaginationParams & { zoneId?: string; binType?: string; status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.zoneId) searchParams.set('zoneId', params.zoneId);
  if (params?.binType) searchParams.set('binType', params.binType);
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/advanced-inventory/bins?${searchParams}`);
}

export async function getAdvancedStorageBin(id: string) {
  return request<any>(`/advanced-inventory/bins/${id}`);
}

export async function createAdvancedStorageBin(data: {
  zoneId: string;
  code: string;
  name?: string;
  binType: string;
  aisle?: string;
  rack?: string;
  level?: string;
  position?: string;
  maxWeight?: string;
  maxVolume?: string;
  mixedItems?: boolean;
  pickSequence?: number;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/bins', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdvancedStorageBin(id: string, data: Partial<{
  name: string;
  binType: string;
  aisle: string;
  rack: string;
  level: string;
  position: string;
  maxWeight: string;
  maxVolume: string;
  mixedItems: boolean;
  pickSequence: number;
  status: string;
  metadata: any;
}>) {
  return request<any>(`/advanced-inventory/bins/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAdvancedStorageBin(id: string) {
  return request<any>(`/advanced-inventory/bins/${id}`, { method: 'DELETE' });
}

// Bin Contents (Advanced Inventory)
export async function getAdvancedBinContents(params?: PaginationParams & { binId?: string; itemType?: string; itemId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.binId) searchParams.set('binId', params.binId);
  if (params?.itemType) searchParams.set('itemType', params.itemType);
  if (params?.itemId) searchParams.set('itemId', params.itemId);
  return request<PaginatedResponse<any>>(`/advanced-inventory/bin-contents?${searchParams}`);
}

export async function createBinContent(data: {
  binId: string;
  itemType: string;
  itemId: string;
  lotNumber?: string;
  quantity: string;
  unit: string;
  expirationDate?: string;
  receivedDate?: string;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/bin-contents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBinContent(id: string, data: Partial<{
  quantity: string;
  lotNumber: string;
  expirationDate: string;
  status: string;
  metadata: any;
}>) {
  return request<any>(`/advanced-inventory/bin-contents/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBinContent(id: string) {
  return request<any>(`/advanced-inventory/bin-contents/${id}`, { method: 'DELETE' });
}

// Inventory Movements
export async function getAdvancedInventoryMovements(params?: PaginationParams & { movementType?: string; status?: string; fromBinId?: string; toBinId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.movementType) searchParams.set('movementType', params.movementType);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.fromBinId) searchParams.set('fromBinId', params.fromBinId);
  if (params?.toBinId) searchParams.set('toBinId', params.toBinId);
  return request<PaginatedResponse<any>>(`/advanced-inventory/movements?${searchParams}`);
}

export async function getInventoryMovement(id: string) {
  return request<any>(`/advanced-inventory/movements/${id}`);
}

export async function createInventoryMovement(data: {
  movementType: string;
  itemType: string;
  itemId: string;
  fromBinId?: string;
  toBinId?: string;
  quantity: string;
  unit: string;
  lotNumber?: string;
  reason?: string;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
}) {
  return request<any>('/advanced-inventory/movements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInventoryMovement(id: string, data: Partial<{
  status: string;
  completedAt: string;
  completedBy: string;
  notes: string;
}>) {
  return request<any>(`/advanced-inventory/movements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Cycle Count Plans
export async function getAdvancedCycleCountPlans(params?: PaginationParams & { status?: string; countType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.countType) searchParams.set('countType', params.countType);
  return request<PaginatedResponse<any>>(`/advanced-inventory/cycle-plans?${searchParams}`);
}

export async function getCycleCountPlan(id: string) {
  return request<any>(`/advanced-inventory/cycle-plans/${id}`);
}

export async function createCycleCountPlan(data: {
  name: string;
  countType: string;
  warehouseId?: string;
  zoneIds?: string[];
  abcClasses?: string[];
  scheduledDate: string;
  frequency?: string;
  instructions?: string;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/cycle-plans', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCycleCountPlan(id: string, data: Partial<{
  name: string;
  countType: string;
  scheduledDate: string;
  frequency: string;
  status: string;
  instructions: string;
  metadata: any;
}>) {
  return request<any>(`/advanced-inventory/cycle-plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCycleCountPlan(id: string) {
  return request<any>(`/advanced-inventory/cycle-plans/${id}`, { method: 'DELETE' });
}

// Cycle Count Tasks
export async function getAdvancedCycleCountTasks(params?: PaginationParams & { planId?: string; status?: string; assignedTo?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.planId) searchParams.set('planId', params.planId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  return request<PaginatedResponse<any>>(`/advanced-inventory/cycle-tasks?${searchParams}`);
}

export async function getCycleCountTask(id: string) {
  return request<any>(`/advanced-inventory/cycle-tasks/${id}`);
}

export async function createCycleCountTask(data: {
  planId: string;
  binId: string;
  assignedTo?: string;
  priority?: number;
  dueDate?: string;
  instructions?: string;
}) {
  return request<any>('/advanced-inventory/cycle-tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCycleCountTask(id: string, data: Partial<{
  assignedTo: string;
  status: string;
  priority: number;
  startedAt: string;
  completedAt: string;
  notes: string;
}>) {
  return request<any>(`/advanced-inventory/cycle-tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Cycle Count Results
export async function getAdvancedCycleCountResults(params?: PaginationParams & { taskId?: string; hasVariance?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.taskId) searchParams.set('taskId', params.taskId);
  if (params?.hasVariance !== undefined) searchParams.set('hasVariance', params.hasVariance.toString());
  return request<PaginatedResponse<any>>(`/advanced-inventory/cycle-results?${searchParams}`);
}

export async function createCycleCountResult(data: {
  taskId: string;
  binContentId: string;
  systemQuantity: string;
  countedQuantity: string;
  unit: string;
  notes?: string;
}) {
  return request<any>('/advanced-inventory/cycle-results', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCycleCountResult(id: string, data: Partial<{
  countedQuantity: string;
  varianceApproved: boolean;
  approvedBy: string;
  approvedAt: string;
  adjustmentMade: boolean;
  notes: string;
}>) {
  return request<any>(`/advanced-inventory/cycle-results/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ABC Classifications
export async function getAbcClassifications(params?: PaginationParams & { status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/advanced-inventory/abc-classifications?${searchParams}`);
}

export async function getAbcClassification(id: string) {
  return request<any>(`/advanced-inventory/abc-classifications/${id}`);
}

export async function createAbcClassification(data: {
  name: string;
  analysisType: string;
  periodMonths: number;
  aPercentage: string;
  bPercentage: string;
  cPercentage: string;
  criteria?: any;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/abc-classifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAbcClassification(id: string, data: Partial<{
  name: string;
  analysisType: string;
  periodMonths: number;
  aPercentage: string;
  bPercentage: string;
  cPercentage: string;
  criteria: any;
  status: string;
  metadata: any;
}>) {
  return request<any>(`/advanced-inventory/abc-classifications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAbcClassification(id: string) {
  return request<any>(`/advanced-inventory/abc-classifications/${id}`, { method: 'DELETE' });
}

// Inventory Reservations
export async function getAdvancedInventoryReservations(params?: PaginationParams & { itemType?: string; itemId?: string; status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.itemType) searchParams.set('itemType', params.itemType);
  if (params?.itemId) searchParams.set('itemId', params.itemId);
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/advanced-inventory/reservations?${searchParams}`);
}

export async function getInventoryReservation(id: string) {
  return request<any>(`/advanced-inventory/reservations/${id}`);
}

export async function createInventoryReservation(data: {
  itemType: string;
  itemId: string;
  binContentId?: string;
  quantity: string;
  unit: string;
  referenceType: string;
  referenceId: string;
  expiresAt?: string;
  priority?: number;
  notes?: string;
}) {
  return request<any>('/advanced-inventory/reservations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInventoryReservation(id: string, data: Partial<{
  quantity: string;
  status: string;
  expiresAt: string;
  fulfilledAt: string;
  notes: string;
}>) {
  return request<any>(`/advanced-inventory/reservations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteInventoryReservation(id: string) {
  return request<any>(`/advanced-inventory/reservations/${id}`, { method: 'DELETE' });
}

// Inventory Snapshots
export async function getAdvancedInventorySnapshots(params?: PaginationParams & { snapshotType?: string; warehouseId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.snapshotType) searchParams.set('snapshotType', params.snapshotType);
  if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  return request<PaginatedResponse<any>>(`/advanced-inventory/snapshots?${searchParams}`);
}

export async function getInventorySnapshot(id: string) {
  return request<any>(`/advanced-inventory/snapshots/${id}`);
}

export async function createInventorySnapshot(data: {
  snapshotType: string;
  warehouseId?: string;
  description?: string;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/snapshots', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Inventory Aging
export async function getAdvancedInventoryAging(params?: PaginationParams & { itemType?: string; binId?: string; agingDaysMin?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.itemType) searchParams.set('itemType', params.itemType);
  if (params?.binId) searchParams.set('binId', params.binId);
  if (params?.agingDaysMin) searchParams.set('agingDaysMin', params.agingDaysMin.toString());
  return request<PaginatedResponse<any>>(`/advanced-inventory/aging?${searchParams}`);
}

export async function getInventoryAgingItem(id: string) {
  return request<any>(`/advanced-inventory/aging/${id}`);
}

export async function updateInventoryAgingItem(id: string, data: Partial<{
  actionRequired: string;
  actionTaken: string;
  actionDate: string;
  notes: string;
}>) {
  return request<any>(`/advanced-inventory/aging/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Stock Level Alerts
export async function getAdvancedStockAlerts(params?: PaginationParams & { alertType?: string; severity?: string; status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.alertType) searchParams.set('alertType', params.alertType);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/advanced-inventory/stock-alerts?${searchParams}`);
}

export async function getStockAlert(id: string) {
  return request<any>(`/advanced-inventory/stock-alerts/${id}`);
}

export async function createStockAlert(data: {
  itemType: string;
  itemId: string;
  alertType: string;
  severity: string;
  thresholdValue?: string;
  currentValue?: string;
  message: string;
  autoReorder?: boolean;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/stock-alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStockAlert(id: string, data: Partial<{
  severity: string;
  status: string;
  acknowledgedBy: string;
  acknowledgedAt: string;
  resolvedBy: string;
  resolvedAt: string;
  resolution: string;
  metadata: any;
}>) {
  return request<any>(`/advanced-inventory/stock-alerts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Stock Alert History
export async function getAdvancedStockAlertHistory(params?: PaginationParams & { alertId?: string; actionType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.alertId) searchParams.set('alertId', params.alertId);
  if (params?.actionType) searchParams.set('actionType', params.actionType);
  return request<PaginatedResponse<any>>(`/advanced-inventory/stock-alert-history?${searchParams}`);
}

// Inventory KPIs
export async function getAdvancedInventoryKpis(params?: PaginationParams & { warehouseId?: string; kpiType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  if (params?.kpiType) searchParams.set('kpiType', params.kpiType);
  return request<PaginatedResponse<any>>(`/advanced-inventory/kpis?${searchParams}`);
}

export async function getInventoryKpi(id: string) {
  return request<any>(`/advanced-inventory/kpis/${id}`);
}

export async function createInventoryKpi(data: {
  warehouseId?: string;
  kpiType: string;
  periodStart: string;
  periodEnd: string;
  targetValue?: string;
  actualValue?: string;
  unit?: string;
  dimensions?: any;
  metadata?: any;
}) {
  return request<any>('/advanced-inventory/kpis', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Dashboard
export async function getAdvancedInventoryDashboard() {
  return request<{
    totalZones: number;
    totalBins: number;
    totalMovements: number;
    pendingCounts: number;
    activeAlerts: number;
    lowStockItems: number;
    recentMovements: any[];
    recentAlerts: any[];
  }>('/advanced-inventory/dashboard');
}

// ==========================================
// Phase 47: Production Analytics & OEE
// ==========================================

// Production Lines (Analytics) - uses Phase 30 productionLines table
export async function getPAProductionLines(params?: PaginationParams & { status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/production-analytics/lines?${searchParams}`);
}

export async function getPAProductionLine(id: string) {
  return request<any>(`/production-analytics/lines/${id}`);
}

export async function createPAProductionLine(data: {
  lineCode: string;
  lineName: string;
  lineType?: string;
  description?: string;
  location?: string;
  warehouseId?: string;
  capacityPerHour?: string;
  capacityUnit?: string;
  standardCrewSize?: number;
  operatingHoursPerDay?: string;
  operatingDaysPerWeek?: number;
  capabilities?: string[];
  metadata?: any;
}) {
  return request<any>('/production-analytics/lines', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePAProductionLine(id: string, data: Partial<{
  lineName: string;
  lineType: string;
  description: string;
  location: string;
  capacityPerHour: string;
  capacityUnit: string;
  standardCrewSize: number;
  operatingHoursPerDay: string;
  operatingDaysPerWeek: number;
  isActive: boolean;
  capabilities: string[];
  metadata: any;
}>) {
  return request<any>(`/production-analytics/lines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePAProductionLine(id: string) {
  return request<any>(`/production-analytics/lines/${id}`, { method: 'DELETE' });
}

// Production Shifts (Analytics)
export async function getPAProductionShifts() {
  return request<{ data: any[] }>('/production-analytics/shifts');
}

export async function createPAProductionShift(data: {
  shiftName: string;
  shiftCode: string;
  startTime: string;
  endTime: string;
  breakMinutes?: number;
  daysOfWeek?: number[];
  color?: string;
}) {
  return request<any>('/production-analytics/shifts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePAProductionShift(id: string, data: Partial<{
  shiftName: string;
  shiftCode: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  daysOfWeek: number[];
  isActive: boolean;
  color: string;
}>) {
  return request<any>(`/production-analytics/shifts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePAProductionShift(id: string) {
  return request<any>(`/production-analytics/shifts/${id}`, { method: 'DELETE' });
}

// Production Runs (Analytics)
export async function getPAProductionRuns(params?: PaginationParams & { lineId?: string; status?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<PaginatedResponse<any>>(`/production-analytics/runs?${searchParams}`);
}

export async function getPAProductionRun(id: string) {
  return request<any>(`/production-analytics/runs/${id}`);
}

export async function createPAProductionRun(data: {
  productionLineId: string;
  productionOrderId?: string;
  shiftId?: string;
  formulaId?: string;
  formulaCode?: string;
  formulaName?: string;
  batchNumber?: string;
  status?: string;
  plannedStart?: string;
  plannedEnd?: string;
  plannedQuantity?: string;
  unit?: string;
  plannedRate?: string;
  operators?: any[];
  notes?: string;
}) {
  return request<any>('/production-analytics/runs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePAProductionRun(id: string, data: Partial<{
  status: string;
  actualStart: string;
  actualEnd: string;
  actualQuantity: string;
  goodQuantity: string;
  rejectQuantity: string;
  reworkQuantity: string;
  actualRate: string;
  operators: any[];
  notes: string;
}>) {
  return request<any>(`/production-analytics/runs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Downtime Events
export async function getDowntimeEvents(params?: PaginationParams & { lineId?: string; runId?: string; category?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.runId) searchParams.set('runId', params.runId);
  if (params?.category) searchParams.set('category', params.category);
  return request<PaginatedResponse<any>>(`/production-analytics/downtime?${searchParams}`);
}

export async function getDowntimeEvent(id: string) {
  return request<any>(`/production-analytics/downtime/${id}`);
}

export async function createDowntimeEvent(data: {
  productionLineId: string;
  productionRunId?: string;
  downtimeCategory: string;
  downtimeReasonId?: string;
  reasonCode?: string;
  reasonDescription?: string;
  startTime: string;
  endTime?: string;
  isPlanned?: boolean;
  impactLevel?: string;
  equipmentId?: string;
  equipmentName?: string;
  notes?: string;
}) {
  return request<any>('/production-analytics/downtime', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDowntimeEvent(id: string, data: Partial<{
  endTime: string;
  startTime: string;
  rootCause: string;
  correctiveAction: string;
  preventiveAction: string;
  costImpact: string;
  notes: string;
}>) {
  return request<any>(`/production-analytics/downtime/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Downtime Reasons
export async function getDowntimeReasons(params?: { category?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  return request<{ data: any[] }>(`/production-analytics/downtime-reasons?${searchParams}`);
}

export async function createDowntimeReason(data: {
  reasonCode: string;
  reasonName: string;
  category: string;
  subcategory?: string;
  description?: string;
  isPlanned?: boolean;
  requiresRootCause?: boolean;
  defaultDurationMinutes?: number;
  escalationThresholdMinutes?: number;
  displayOrder?: number;
}) {
  return request<any>('/production-analytics/downtime-reasons', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDowntimeReason(id: string, data: Partial<{
  reasonName: string;
  category: string;
  subcategory: string;
  description: string;
  isPlanned: boolean;
  requiresRootCause: boolean;
  isActive: boolean;
  displayOrder: number;
}>) {
  return request<any>(`/production-analytics/downtime-reasons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteDowntimeReason(id: string) {
  return request<any>(`/production-analytics/downtime-reasons/${id}`, { method: 'DELETE' });
}

// OEE Calculations
export async function getOeeCalculations(params?: PaginationParams & { lineId?: string; periodType?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.periodType) searchParams.set('periodType', params.periodType);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<PaginatedResponse<any>>(`/production-analytics/oee?${searchParams}`);
}

export async function getOeeCalculation(id: string) {
  return request<any>(`/production-analytics/oee/${id}`);
}

export async function createOeeCalculation(data: {
  productionLineId: string;
  calculationDate: string;
  shiftId?: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  totalTime: string;
  plannedProductionTime: string;
  operatingTime: string;
  plannedDowntime?: string;
  unplannedDowntime?: string;
  totalCount?: string;
  goodCount?: string;
  rejectCount?: string;
  idealCycleTime?: string;
  notes?: string;
}) {
  return request<any>('/production-analytics/oee', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Speed Loss Events
export async function getSpeedLossEvents(params?: { runId?: string; lineId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.runId) searchParams.set('runId', params.runId);
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  return request<{ data: any[] }>(`/production-analytics/speed-losses?${searchParams}`);
}

export async function createSpeedLossEvent(data: {
  productionRunId: string;
  productionLineId: string;
  startTime: string;
  endTime?: string;
  expectedRate: string;
  actualRate: string;
  lossCategory?: string;
  reason?: string;
  unitsLost?: string;
}) {
  return request<any>('/production-analytics/speed-losses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Quality Loss Events
export async function getQualityLossEvents(params?: PaginationParams & { runId?: string; lineId?: string; severity?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.runId) searchParams.set('runId', params.runId);
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.severity) searchParams.set('severity', params.severity);
  return request<PaginatedResponse<any>>(`/production-analytics/quality-losses?${searchParams}`);
}

export async function createQualityLossEvent(data: {
  productionRunId: string;
  productionLineId: string;
  detectedAt: string;
  defectType: string;
  defectCode?: string;
  defectDescription?: string;
  quantityAffected: string;
  unit?: string;
  severity?: string;
  detectionPoint?: string;
  lotNumbers?: string[];
}) {
  return request<any>('/production-analytics/quality-losses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQualityLossEvent(id: string, data: Partial<{
  disposition: string;
  rootCause: string;
  correctiveAction: string;
  costImpact: string;
}>) {
  return request<any>(`/production-analytics/quality-losses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Operator Performance
export async function getOperatorPerformance(params?: PaginationParams & { userId?: string; lineId?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.userId) searchParams.set('userId', params.userId);
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<PaginatedResponse<any>>(`/production-analytics/operator-performance?${searchParams}`);
}

export async function createOperatorPerformance(data: {
  userId: string;
  productionLineId?: string;
  productionRunId?: string;
  shiftId?: string;
  performanceDate: string;
  hoursWorked?: string;
  unitsProduced?: string;
  goodUnits?: string;
  rejectUnits?: string;
  downtimeIncidents?: number;
  safetyIncidents?: number;
  attendanceStatus?: string;
  supervisorNotes?: string;
}) {
  return request<any>('/production-analytics/operator-performance', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOperatorPerformance(id: string, data: Partial<{
  hoursWorked: string;
  unitsProduced: string;
  goodUnits: string;
  rejectUnits: string;
  supervisorNotes: string;
  selfAssessment: string;
  goalsMet: any;
}>) {
  return request<any>(`/production-analytics/operator-performance/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Production Targets
export async function getProductionTargets(params?: PaginationParams & { lineId?: string; targetType?: string; status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.targetType) searchParams.set('targetType', params.targetType);
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/production-analytics/targets?${searchParams}`);
}

export async function createProductionTarget(data: {
  targetName: string;
  targetType: string;
  productionLineId?: string;
  formulaId?: string;
  shiftId?: string;
  periodType: string;
  startDate: string;
  endDate?: string;
  targetValue: string;
  unit?: string;
  minimumThreshold?: string;
  stretchGoal?: string;
  ownerId?: string;
  notes?: string;
}) {
  return request<any>('/production-analytics/targets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProductionTarget(id: string, data: Partial<{
  targetName: string;
  targetValue: string;
  currentValue: string;
  minimumThreshold: string;
  stretchGoal: string;
  status: string;
  notes: string;
}>) {
  return request<any>(`/production-analytics/targets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProductionTarget(id: string) {
  return request<any>(`/production-analytics/targets/${id}`, { method: 'DELETE' });
}

// Production Alerts (PA prefix to avoid conflict with existing getProductionAlerts)
export async function getPAProductionAlerts(params?: PaginationParams & { alertType?: string; severity?: string; unresolved?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.alertType) searchParams.set('alertType', params.alertType);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.unresolved) searchParams.set('unresolved', 'true');
  return request<PaginatedResponse<any>>(`/production-analytics/alerts?${searchParams}`);
}

export async function createPAProductionAlert(data: {
  alertType: string;
  severity: string;
  productionLineId?: string;
  productionRunId?: string;
  title: string;
  message: string;
  metricName?: string;
  thresholdValue?: string;
  actualValue?: string;
}) {
  return request<any>('/production-analytics/alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePAProductionAlert(id: string, data: Partial<{
  acknowledged: boolean;
  resolved: boolean;
  resolutionNotes: string;
}>) {
  return request<any>(`/production-analytics/alerts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Analytics Snapshots
export async function getProductionAnalyticsSnapshots(params?: PaginationParams & { lineId?: string; snapshotType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.lineId) searchParams.set('lineId', params.lineId);
  if (params?.snapshotType) searchParams.set('snapshotType', params.snapshotType);
  return request<PaginatedResponse<any>>(`/production-analytics/snapshots?${searchParams}`);
}

// Dashboard
export async function getProductionAnalyticsDashboard() {
  return request<{
    totalLines: number;
    activeRuns: number;
    todayAvgOee: string | null;
    unresolvedAlerts: number;
    recentRuns: any[];
    recentDowntime: any[];
  }>('/production-analytics/dashboard');
}

// ==========================================
// Phase 48: Shelf Life & Expiration Management
// ==========================================

// Shelf Life Definitions
export async function getShelfLifeDefinitions(params?: PaginationParams & { entityType?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<PaginatedResponse<any>>(`/shelf-life/definitions?${searchParams}`);
}

export async function getShelfLifeDefinition(id: string) {
  return request<any>(`/shelf-life/definitions/${id}`);
}

export async function createShelfLifeDefinition(data: {
  entityType: string;
  entityId?: string;
  category?: string;
  shelfLifeDays: number;
  shelfLifeUnit?: string;
  storageConditions?: string;
  temperatureMin?: string;
  temperatureMax?: string;
  humidityMin?: string;
  humidityMax?: string;
  retestIntervalDays?: number;
  retestRequired?: boolean;
  extensionAllowed?: boolean;
  maxExtensions?: number;
  extensionDaysPerRetest?: number;
  packagingRequirements?: string;
  specialInstructions?: string;
  regulatoryBasis?: string;
}) {
  return request<any>('/shelf-life/definitions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateShelfLifeDefinition(id: string, data: Partial<{
  shelfLifeDays: number;
  shelfLifeUnit: string;
  storageConditions: string;
  temperatureMin: string;
  temperatureMax: string;
  retestIntervalDays: number;
  retestRequired: boolean;
  extensionAllowed: boolean;
  maxExtensions: number;
  isActive: boolean;
}>) {
  return request<any>(`/shelf-life/definitions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteShelfLifeDefinition(id: string) {
  return request<any>(`/shelf-life/definitions/${id}`, { method: 'DELETE' });
}

// Lot Expirations
export async function getLotExpirations(params?: PaginationParams & { status?: string; entityType?: string; warehouseId?: string; expiringWithinDays?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  if (params?.expiringWithinDays) searchParams.set('expiringWithinDays', params.expiringWithinDays.toString());
  return request<PaginatedResponse<any>>(`/shelf-life/lot-expirations?${searchParams}`);
}

export async function getLotExpiration(id: string) {
  return request<any>(`/shelf-life/lot-expirations/${id}`);
}

export async function createLotExpiration(data: {
  lotId: string;
  lotNumber: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  manufactureDate?: string;
  receiptDate?: string;
  originalExpirationDate: string;
  currentExpirationDate: string;
  shelfLifeDefinitionId?: string;
  currentQuantity?: string;
  quantityUnit?: string;
  warehouseId?: string;
  locationId?: string;
  storageConditions?: string;
}) {
  return request<any>('/shelf-life/lot-expirations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateLotExpiration(id: string, data: Partial<{
  currentExpirationDate: string;
  status: string;
  currentQuantity: string;
  quarantineReason: string;
  notes: string;
}>) {
  return request<any>(`/shelf-life/lot-expirations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Shelf Life Extensions
export async function getShelfLifeExtensions(params?: PaginationParams & { lotExpirationId?: string; approvalStatus?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.lotExpirationId) searchParams.set('lotExpirationId', params.lotExpirationId);
  if (params?.approvalStatus) searchParams.set('approvalStatus', params.approvalStatus);
  return request<PaginatedResponse<any>>(`/shelf-life/extensions?${searchParams}`);
}

export async function createShelfLifeExtension(data: {
  lotExpirationId: string;
  lotNumber: string;
  extensionNumber: number;
  previousExpirationDate: string;
  newExpirationDate: string;
  extensionDays: number;
  extensionType: string;
  retestDate?: string;
  retestResult?: string;
  justification: string;
  supportingDocuments?: any[];
}) {
  return request<any>('/shelf-life/extensions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateShelfLifeExtension(id: string, data: Partial<{
  approvalStatus: string;
  rejectionReason: string;
  newExpirationDate: string;
}>) {
  return request<any>(`/shelf-life/extensions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Retest Schedules
export async function getRetestSchedules(params?: PaginationParams & { status?: string; priority?: string; upcoming?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.priority) searchParams.set('priority', params.priority);
  if (params?.upcoming) searchParams.set('upcoming', 'true');
  return request<PaginatedResponse<any>>(`/shelf-life/retest-schedules?${searchParams}`);
}

export async function createRetestSchedule(data: {
  lotExpirationId: string;
  lotNumber: string;
  scheduledDate: string;
  retestType: string;
  testSpecifications?: any[];
  priority?: string;
  assignedLabId?: string;
  assignedTo?: string;
  notes?: string;
}) {
  return request<any>('/shelf-life/retest-schedules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRetestSchedule(id: string, data: Partial<{
  status: string;
  result: string;
  resultSummary: string;
  certificateNumber: string;
  extensionRecommended: boolean;
  recommendedExtensionDays: number;
}>) {
  return request<any>(`/shelf-life/retest-schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// FIFO/FEFO Rules
export async function getFifoFefoRules(params?: { isActive?: boolean; ruleType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  if (params?.ruleType) searchParams.set('ruleType', params.ruleType);
  return request<{ data: any[] }>(`/shelf-life/fifo-fefo-rules?${searchParams}`);
}

export async function createFifoFefoRule(data: {
  ruleName: string;
  ruleType: string;
  entityType?: string;
  category?: string;
  warehouseId?: string;
  priority?: number;
  minRemainingShelfLifeDays?: number;
  minRemainingShelfLifePercent?: string;
  excludeNearExpiry?: boolean;
  nearExpiryThresholdDays?: number;
  allowOverride?: boolean;
  overrideRequiresApproval?: boolean;
  notes?: string;
}) {
  return request<any>('/shelf-life/fifo-fefo-rules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFifoFefoRule(id: string, data: Partial<{
  ruleName: string;
  ruleType: string;
  priority: number;
  minRemainingShelfLifeDays: number;
  excludeNearExpiry: boolean;
  nearExpiryThresholdDays: number;
  isActive: boolean;
}>) {
  return request<any>(`/shelf-life/fifo-fefo-rules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteFifoFefoRule(id: string) {
  return request<any>(`/shelf-life/fifo-fefo-rules/${id}`, { method: 'DELETE' });
}

// Expiration Alert Configs
export async function getExpirationAlertConfigs(params?: { isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/shelf-life/alert-configs?${searchParams}`);
}

export async function createExpirationAlertConfig(data: {
  configName: string;
  entityType?: string;
  category?: string;
  warehouseId?: string;
  alertThresholdDays: number;
  alertType: string;
  notificationChannels?: string[];
  recipientRoles?: string[];
  recipientUsers?: string[];
  emailTemplate?: string;
  includeQuantity?: boolean;
  includeLocation?: boolean;
  includeValue?: boolean;
  autoQuarantine?: boolean;
  quarantineThresholdDays?: number;
}) {
  return request<any>('/shelf-life/alert-configs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateExpirationAlertConfig(id: string, data: Partial<{
  configName: string;
  alertThresholdDays: number;
  alertType: string;
  notificationChannels: string[];
  isActive: boolean;
}>) {
  return request<any>(`/shelf-life/alert-configs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteExpirationAlertConfig(id: string) {
  return request<any>(`/shelf-life/alert-configs/${id}`, { method: 'DELETE' });
}

// Expiration Alerts
export async function getExpirationAlerts(params?: PaginationParams & { alertType?: string; unresolved?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.alertType) searchParams.set('alertType', params.alertType);
  if (params?.unresolved) searchParams.set('unresolved', 'true');
  return request<PaginatedResponse<any>>(`/shelf-life/alerts?${searchParams}`);
}

export async function createExpirationAlert(data: {
  lotExpirationId: string;
  alertConfigId?: string;
  lotNumber: string;
  entityType: string;
  entityName?: string;
  alertType: string;
  daysUntilExpiry?: number;
  expirationDate: string;
  quantity?: string;
  quantityUnit?: string;
  message: string;
}) {
  return request<any>('/shelf-life/alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateExpirationAlert(id: string, data: Partial<{
  acknowledged: boolean;
  actionTaken: string;
  resolved: boolean;
  resolutionNotes: string;
}>) {
  return request<any>(`/shelf-life/alerts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Quarantine Records
export async function getQuarantineRecords(params?: PaginationParams & { disposition?: string; quarantineReason?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.disposition) searchParams.set('disposition', params.disposition);
  if (params?.quarantineReason) searchParams.set('quarantineReason', params.quarantineReason);
  return request<PaginatedResponse<any>>(`/shelf-life/quarantine?${searchParams}`);
}

export async function getQuarantineRecord(id: string) {
  return request<any>(`/shelf-life/quarantine/${id}`);
}

export async function createQuarantineRecord(data: {
  lotExpirationId?: string;
  lotId?: string;
  lotNumber: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  quarantineReason: string;
  quarantineType?: string;
  quantity: string;
  quantityUnit?: string;
  originalLocation?: string;
  quarantineLocation?: string;
  warehouseId?: string;
  expectedResolutionDate?: string;
  notes?: string;
}) {
  return request<any>('/shelf-life/quarantine', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuarantineRecord(id: string, data: Partial<{
  disposition: string;
  dispositionNotes: string;
  disposalMethod: string;
  rootCause: string;
  correctiveAction: string;
}>) {
  return request<any>(`/shelf-life/quarantine/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Disposal Records
export async function getDisposalRecords(params?: PaginationParams & { disposalReason?: string; disposalMethod?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.disposalReason) searchParams.set('disposalReason', params.disposalReason);
  if (params?.disposalMethod) searchParams.set('disposalMethod', params.disposalMethod);
  return request<PaginatedResponse<any>>(`/shelf-life/disposals?${searchParams}`);
}

export async function createDisposalRecord(data: {
  quarantineRecordId?: string;
  lotExpirationId?: string;
  lotNumber: string;
  entityType: string;
  entityName?: string;
  disposalReason: string;
  quantity: string;
  quantityUnit?: string;
  disposalMethod: string;
  disposalVendor?: string;
  disposalLocation?: string;
  disposalDate: string;
  disposalCertificateNumber?: string;
  environmentalCompliance?: boolean;
  cost?: string;
  recoveredValue?: string;
  authorizedBy?: string;
  witnessedBy?: string;
  notes?: string;
}) {
  return request<any>('/shelf-life/disposals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Shelf Life Analytics
export async function getShelfLifeAnalytics(params?: PaginationParams & { periodType?: string; entityType?: string; warehouseId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.periodType) searchParams.set('periodType', params.periodType);
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  return request<PaginatedResponse<any>>(`/shelf-life/analytics?${searchParams}`);
}

// Shelf Life Dashboard
export async function getShelfLifeDashboard() {
  return request<{
    totalActiveLots: number;
    expiringIn30Days: number;
    expiredLots: number;
    quarantinedLots: number;
    pendingRetests: number;
    unresolvedAlerts: number;
    recentExpirations: any[];
    recentQuarantine: any[];
  }>('/shelf-life/dashboard');
}
